import { Button, Input, Select, message, Modal } from 'antd';
import React, { useState, useEffect } from 'react';
import { postRequest } from '../../utils/APICall';
import DatePickerComponent from '../DatePicker';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { SelectEmployee } from '../SelectInput';

const { TextArea } = Input;

const LeaveForm = ({ editingData, onFinishEdit }) => {
  const [employeeId, setEmployeeId] = useState(null);
  const [leaveType, setLeaveType] = useState(null);
  const [leaveStatus, setLeaveStatus] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [editId, setEditId] = useState(0);
  const { users } = useSelector((state) => state.user || {});
  const { allEmployee } = useSelector((state) => state.employees);

  function removeDuplicatesByID(array) {
    const seen = new Set();
    return array.filter((item) => {
      if (seen.has(item.ID)) {
        return false;
      }
      seen.add(item.ID);
      return true;
    });
  }
  const filterData = removeDuplicatesByID(allEmployee);

  useEffect(() => {
    if (editingData) {
      setEmployeeId(editingData.FKEMPID);
      setLeaveStatus(editingData?.LEAVE_STATUS);
      setLeaveType(editingData.LEAVETYPE === 1 ? 'Annualleave' : 'Sickleave');
      setFromDate(moment(editingData.FROMDATE).format("DD-MMMM-YYYY"));
      setToDate(moment(editingData.TODATE).format("DD-MMMM-YYYY"));
      setRemarks(editingData.REMARKS);
      setEditId(editingData.ID);
    }
  }, [editingData]);

  const isFormValid = () => {
    return (
      employeeId &&
      leaveType &&
      leaveStatus &&
      fromDate &&
      toDate &&
      remarks.trim() !== ''
    );
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      message.error('Please fill in all fields');
      return;
    }

    const payload = {
      vEmpid: employeeId,
      vLeavetype: leaveType === 'Annualleave' ? 1 : 2,
      vfromdate: fromDate,
      vtodate: toDate,
      vRemarks: remarks,
      vCreatedby: `${users?.FIRSTNAME} ${users?.LASTNAME} | ${moment().format("DD-MM-YYYY HH:mm")}`,
      veditid: editId || 0,
      leave_status: editId == 0 ? leaveStatus : leaveStatus,
    };

    try {
      const res = await postRequest('addSickLeave', payload);
      if (res.success) {
        message.success(res.message);
        setEmployeeId(null);
        setLeaveType(null);
        setLeaveStatus(null);
        setFromDate(null);
        setToDate(null);
        setRemarks('');
        setEditId(0);
        onFinishEdit?.();
      } else {
        message.error(res.message || 'Something went wrong');
      }
    } catch (err) {
      message.error('Failed to submit leave');
    }
  };

  const showConfirm = () => {
    Modal.confirm({
      title: editId ? 'Confirm Update' : 'Confirm Submission',
      content: editId ? 'Are you sure you want to update this leave?' : 'Are you sure you want to submit this leave?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: handleSubmit,
    });
  };

  return (
   <span className='flex md:justify-start flex-wrap gap-4 items-center px-4 py-4'>

  {/* Employee Name */}
  <span className='w-full sm:w-fit'>
    <label className='block font-semibold mb-1 text-gray-800 dark:text-gray-200'>
      Employee Name <span className="text-red-500">*</span>
    </label>
    <SelectEmployee
      employeeList={filterData}
      onChange={(value) => setEmployeeId(value)}
      selectedEmployee={employeeId}
      customStyle="w-full sm:w-[250px] "
      allLabel={{}}
    />
  </span>

  {/* Leave Type */}
  <span className='w-full sm:w-fit'>
    <label className='block font-semibold mb-1 text-gray-800 dark:text-gray-200'>
      Leave Type <span className="text-red-500">*</span>
    </label>
    <Select
      showSearch
      placeholder="Leave Type"
      optionFilterProp="label"
      value={leaveType}
      onChange={setLeaveType}
      size='large'
      className='w-full sm:w-[250px] '
      options={[
        { value: 'Annualleave', label: 'Annual Leaves' },
        { value: 'Sickleave', label: 'Sick Leaves' },
      ]}
    />
  </span>

  {/* From Date */}
  <span className='w-full sm:w-fit'>
    <label className='block font-semibold mb-1 text-gray-800 dark:text-gray-200'>
      From Date <span className="text-red-500">*</span>
    </label>
    <DatePickerComponent
      date={fromDate}
      onChange={(date, dateString) => setFromDate(moment(dateString).format("DD.MMMM.YYYY"))}
      customStyle="w-full sm:w-[250px] h-10 "
    />
  </span>

  {/* To Date */}
  <span className='w-full sm:w-fit'>
    <label className='block font-semibold mb-1 text-gray-800 dark:text-gray-200'>
      To Date <span className="text-red-500">*</span>
    </label>
    <DatePickerComponent
      date={toDate}
      onChange={(date, dateString) => setToDate(moment(dateString).format("DD.MMMM.YYYY"))}
      customStyle="w-full sm:w-[250px] h-10 "
    />
  </span>

  {/* Leave Status */}
  <span className='w-full sm:w-fit'>
    <label className='block font-semibold mb-1 text-gray-800 dark:text-gray-200'>
      Leave Status <span className="text-red-500">*</span>
    </label>
    <Select
      showSearch
      placeholder="Leave Status"
      optionFilterProp="label"
      value={leaveStatus}
      onChange={setLeaveStatus}
      size='large'
      className='w-full sm:w-[250px] '
      options={[
        { value: "Pending", label: 'Pending' },
        { value: "Approved", label: 'Approved' },
        { value: "Reject", label: 'Reject' },
      ]}
    />
  </span>

  {/* Remarks */}
  <span className='w-full sm:w-fit'>
    <label className='block font-semibold mb-1 text-gray-800 dark:text-gray-200'>
      Remarks <span className="text-red-500">*</span>
    </label>
    <TextArea
      placeholder="Remarks..."
      rows={2}
      className='w-full sm:w-[250px] '
      value={remarks}
      onChange={(e) => setRemarks(e.target.value)}
    />
  </span>

  {/* Submit Button */}
  <span>
    <Button
      color="primary"
      size="large"
      className="sm:ml-4 flex mt-6 text-lg font-bold disabled:text-white disabled:bg-gray-500 dark:text-gray-100  p-4 px-12"
      variant="solid"
      onClick={showConfirm}
      disabled={!isFormValid()}
    >
      {editId ? 'Update' : 'Submit'}
    </Button>
  </span>

</span>

  );
};

export default LeaveForm;